import { IVectorLayerGroupConfig } from "esdm-map/dist/layers/groups/vector/interfaces/IVectorLayerGroupConfig";
import { WfsParamSourceMapServer, IWfsParamSource, WfsBboxAsWktRequestParams } from "esdm-map/dist/layers/vector/sources/wfs";
import { VectorLayerStrategy } from "esdm-map/dist/layers/vector/VectorLayerStrategy";
import { AberdeenshireRecordType, AberdeenshireRecordSubType } from "./AberdeenshireRecordTypes";
import { IWmsLayerGroupConfig } from "esdm-map/dist/layers/groups/wms/interfaces/IWmsLayerGroupConfig";

export class DefaultLayerConfigurationFactory {

    private mapFileUrl: string = "/api/OgcService/hbsmr";

    private paramSource(): IWfsParamSource {
        return new WfsParamSourceMapServer(new WfsBboxAsWktRequestParams(),{srsName: "EPSG:27700"});
    }

    constructor() {
    }

    public layerConfig = (
        recordType: AberdeenshireRecordType,
        recordSubType: AberdeenshireRecordSubType = null,
        opts: IVectorLayerGroupConfig = {}
    ): IVectorLayerGroupConfig | IWmsLayerGroupConfig => {
        let layerConfig: IVectorLayerGroupConfig | IWmsLayerGroupConfig = null;

        switch (recordType) {
            case AberdeenshireRecordType.Monument:
                layerConfig = this.Monument(opts);
                break;         

            case AberdeenshireRecordType.Event:
                layerConfig = this.Event(opts);
                break;   

        }

        if (layerConfig === null && recordSubType !== null) {
            switch (recordSubType) {

                case AberdeenshireRecordSubType.ListedBuilding:
                    layerConfig = this.ListedBuilding();
                    break;

                case AberdeenshireRecordSubType.ScheduledMonument:
                    layerConfig = this.ScheduledMonument();
                    break;    
                                
                case AberdeenshireRecordSubType.GardensAndDesignedLandscapes:
                    layerConfig = this.GardensAndDesignedLandscapes();
                    break; 

                case AberdeenshireRecordSubType.Battlefields:
                    layerConfig = this.Battlefields();
                    break;

                case AberdeenshireRecordSubType.ConservationArea:
                    layerConfig = this.ConservationArea();
                    break; 
            }
        }

        return layerConfig;
    }

    private Monument = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Monuments",
                ref: "Monuments",
                type: AberdeenshireRecordType.Monument,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 100000,
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "MonumentPolygonsWFS",
                        minZoom: 12,
                        maxZoom: 19
                    },
                    {
                        layerName: "MonumentPointsWFS",
                        minZoom: 1,
                        maxZoom: 11
                    }                 
                ]
            }, ...opts
        };
    }

    private Event = (
        opts: IVectorLayerGroupConfig
    ): IVectorLayerGroupConfig => {
        return {
            ...{
                displayName: "Events",
                ref: "Events",
                type: AberdeenshireRecordType.Event,
                strategy: VectorLayerStrategy.Bbox,
                url: this.mapFileUrl,
                wfsParams: this.paramSource(),
                maxFeatureCount: 50000,    
                legend: { line: false },
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "EventPolygonsWFS",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }, ...opts
        };
    }

    private ListedBuilding = (): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: "HES Listed Buildings",
                ref: "ListedBuildings",
                type: AberdeenshireRecordType.Designation,
                subType: AberdeenshireRecordSubType.ListedBuilding,
                url: "/api/OGCService/HESDesignations",
                displayInLayerSwitcher: true,
                legend: { line: false, polygon: false},
                queryable: false,
                info_format: "text/html",
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "7", 
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }
        };
    }

    private ScheduledMonument = (): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: "HES Scheduled Monuments",
                ref: "ScheduledMonuments",
                type: AberdeenshireRecordType.Designation,
                subType: AberdeenshireRecordSubType.ScheduledMonument,
                url: "/api/OGCService/HESDesignations",
                displayInLayerSwitcher: true,
                legend: { point: false, line: false },
                queryable: false,
                info_format: "text/html",
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "2",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }
        };
    }
 
    private GardensAndDesignedLandscapes = (): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: "HES Inventory Designed Landscapes",
                ref: "GardensAndDesignedLandscapes",
                type: AberdeenshireRecordType.Designation,
                subType: AberdeenshireRecordSubType.GardensAndDesignedLandscapes,
                url: "/api/OGCService/HESDesignations",
                displayInLayerSwitcher: true,
                legend: { point: false, line: false },
                queryable: false,
                info_format: "text/xml",
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "3",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }
        };
    }

    
    private ConservationArea = (): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: "HES Conservation Areas",
                ref: "ConservationArea",
                type: AberdeenshireRecordType.Designation,
                subType: AberdeenshireRecordSubType.ConservationArea,
                url: "/api/OGCService/HESDesignations",
                urlParams: { recsubtypecode: AberdeenshireRecordSubType.ConservationArea },
                displayInLayerSwitcher: true,
                legend: { point: false, line: false },
                queryable: false,
                info_format: "text/html",
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "5",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }
        };
    }
    
    private Battlefields = (): IWmsLayerGroupConfig => {
        return {
            ...{
                displayName: "HES Inventory Battlefields",
                ref: "Battlefields",
                type: AberdeenshireRecordType.Designation,
                subType: AberdeenshireRecordSubType.Battlefields,
                url: "/api/OGCService/HESDesignations",
                displayInLayerSwitcher: true,
                legend: { point:false, line: false },
                queryable: false,
                info_format: "text/html",
                visibleOnFirstLoad: false,
                alwaysLoadOnZoom: true,
                layers: [
                    {
                        layerName: "4",
                        minZoom: 1,
                        maxZoom: 19
                    }
                ]
            }
        };
    }


}

